/**
 * Delivery Note Detail Page
 * Epic 63: Szallitolevol reszletei + muveletek
 */

import type { DeliveryNoteStatus, DeliveryNoteType } from '@/api/delivery-notes';
import { DocumentsPanel } from '@/features/documents/components/DocumentsPanel';
import { useDeliveryNote, useLinkToInvoice, useMarkSettled } from '@/hooks/useDeliveryNotes';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const TYPE_COLORS: Record<DeliveryNoteType, string> = {
  STANDARD: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
  CONTRACT: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
  RENTAL_ACCESSORY: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
  CREDIT: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
};

const STATUS_COLORS: Record<DeliveryNoteStatus, string> = {
  OPEN: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
  INVOICED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
  SETTLED: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
  CLOSED: 'bg-gray-100 text-gray-800 dark:bg-[#1f5f6b] dark:text-[#ccfbf1]',
};

const TYPE_LABEL_KEYS: Record<DeliveryNoteType, string> = {
  STANDARD: 'invoice.deliveryNote.typeLabels.standard',
  CONTRACT: 'invoice.deliveryNote.typeLabels.contract',
  RENTAL_ACCESSORY: 'invoice.deliveryNote.typeLabels.rentalAccessory',
  CREDIT: 'invoice.deliveryNote.typeLabels.supplierCredit',
};

const STATUS_LABEL_KEYS: Record<DeliveryNoteStatus, string> = {
  OPEN: 'invoice.deliveryNote.statusLabels.open',
  INVOICED: 'invoice.deliveryNote.statusLabels.invoiced',
  SETTLED: 'invoice.deliveryNote.statusLabels.settled',
  CLOSED: 'invoice.deliveryNote.statusLabels.closed',
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function DeliveryNoteDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { note, isLoading, error } = useDeliveryNote(id);
  const linkMutation = useLinkToInvoice();
  const settleMutation = useMarkSettled();

  const [invoiceId, setInvoiceId] = useState('');
  const [showLinkDialog, setShowLinkDialog] = useState(false);

  const handleLinkToInvoice = async () => {
    if (!id || !invoiceId) return;
    await linkMutation.mutateAsync({ id, noteIds: [id], invoiceId });
    setShowLinkDialog(false);
    setInvoiceId('');
  };

  const handleSettle = async () => {
    if (!id) return;
    await settleMutation.mutateAsync(id);
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-[#ccfbf1]">
        {t('invoice.deliveryNote.loading')}
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500">{error ?? t('invoice.deliveryNote.notFound')}</p>
        <button
          onClick={() => navigate('/delivery-notes')}
          className="mt-4 rounded-lg border px-4 py-2 hover:bg-gray-50 dark:border-[#2d7d8a] dark:hover:bg-[#0f3d47]"
        >
          {t('invoice.deliveryNote.backToList')}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/delivery-notes')}
            className="mb-2 text-sm text-gray-500 hover:text-gray-700 dark:text-[#ccfbf1] dark:hover:text-white"
          >
            &larr; {t('invoice.deliveryNote.backToList')}
          </button>
          <h1 className="text-2xl font-bold">{note.noteNumber}</h1>
        </div>
        <div className="flex gap-2">
          {note.status === 'OPEN' && (
            <button
              onClick={() => setShowLinkDialog(true)}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              {t('invoice.deliveryNote.invoicing')}
            </button>
          )}
          {note.status === 'INVOICED' && (
            <button
              onClick={handleSettle}
              disabled={settleMutation.isPending}
              className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-50"
            >
              {settleMutation.isPending
                ? t('invoice.deliveryNote.processing')
                : t('invoice.deliveryNote.financiallySettled')}
            </button>
          )}
        </div>
      </div>

      {/* Details Card */}
      <div className="rounded-lg border p-6">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <div className="text-sm text-gray-500 dark:text-[#ccfbf1]">
              {t('invoice.deliveryNote.typeFilterLabel')}
            </div>
            <span
              className={`mt-1 inline-block rounded-full px-2 py-1 text-xs font-medium ${TYPE_COLORS[note.type]}`}
            >
              {t(TYPE_LABEL_KEYS[note.type] ?? note.type)}
            </span>
          </div>
          <div>
            <div className="text-sm text-gray-500 dark:text-[#ccfbf1]">
              {t('invoice.deliveryNote.statusLabel')}
            </div>
            <span
              className={`mt-1 inline-block rounded-full px-2 py-1 text-xs font-medium ${STATUS_COLORS[note.status]}`}
            >
              {t(STATUS_LABEL_KEYS[note.status] ?? note.status)}
            </span>
          </div>
          <div>
            <div className="text-sm text-gray-500 dark:text-[#ccfbf1]">{t('table.partner')}</div>
            <div className="mt-1 font-medium">{note.partnerName}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 dark:text-[#ccfbf1]">
              {t('invoice.deliveryNote.amountLabel')}
            </div>
            <div className="mt-1 text-lg font-bold">{formatCurrency(note.totalAmount)}</div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <div className="text-sm text-gray-500 dark:text-[#ccfbf1]">
              {t('invoice.deliveryNote.createdAt')}
            </div>
            <div className="mt-1 text-sm">{new Date(note.createdAt).toLocaleString('hu-HU')}</div>
          </div>
          {note.invoiceId && (
            <div>
              <div className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                {t('invoice.deliveryNote.invoiceId')}
              </div>
              <div className="mt-1 text-sm font-medium">{note.invoiceId}</div>
            </div>
          )}
          {note.isContractPartner && (
            <div>
              <div className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                {t('invoice.deliveryNote.contractPartner')}
              </div>
              <div className="mt-1 text-sm font-medium text-green-600">{t('common.yes')}</div>
            </div>
          )}
          {note.settledManually && (
            <div>
              <div className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                {t('invoice.deliveryNote.manualSettlement')}
              </div>
              <div className="mt-1 text-sm font-medium text-green-600">
                {t('invoice.deliveryNote.settled')}
              </div>
            </div>
          )}
        </div>

        {note.notes && (
          <div className="mt-4 border-t pt-4 dark:border-[#2d7d8a]">
            <div className="text-sm text-gray-500 dark:text-[#ccfbf1]">
              {t('invoice.deliveryNote.notesLabel')}
            </div>
            <div className="mt-1 text-sm">{note.notes}</div>
          </div>
        )}
      </div>

      {/* Items Table */}
      <div className="rounded-lg border">
        <div className="border-b bg-gray-50 px-4 py-3 dark:bg-[#1f5f6b] dark:border-[#2d7d8a]">
          <h2 className="font-medium">
            {t('invoice.deliveryNote.itemsCount', { count: note.items.length })}
          </h2>
        </div>
        <table className="min-w-full divide-y divide-gray-200 dark:divide-[#2d7d8a]">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-[#ccfbf1]">
                {t('invoice.deliveryNote.product')}
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-[#ccfbf1]">
                {t('invoice.deliveryNote.quantity')}
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-[#ccfbf1]">
                {t('invoice.deliveryNote.unitPrice')}
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-[#ccfbf1]">
                {t('invoice.deliveryNote.amount')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-[#2d7d8a]">
            {note.items.map(item => (
              <tr key={item.id}>
                <td className="px-4 py-3 text-sm">
                  <div className="font-medium">{item.productName}</div>
                  <div className="text-xs text-gray-400 dark:text-[#99f6e4]">{item.productId}</div>
                </td>
                <td className="px-4 py-3 text-right text-sm">
                  {item.quantity} {t('common.piece')}
                </td>
                <td className="px-4 py-3 text-right text-sm">{formatCurrency(item.unitPrice)}</td>
                <td className="px-4 py-3 text-right text-sm font-medium">
                  {formatCurrency(item.totalPrice)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 dark:bg-[#1f5f6b]">
              <td colSpan={3} className="px-4 py-3 text-right text-sm font-bold">
                {t('invoice.deliveryNote.totalLabel')}
              </td>
              <td className="px-4 py-3 text-right text-sm font-bold">
                {formatCurrency(note.totalAmount)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Attached Documents (Story 94-9b) */}
      {note.id && (
        <DocumentsPanel
          sourceType="DELIVERY_NOTE"
          sourceId={note.id}
          legalBasis="ACCOUNTING_RECORD"
        />
      )}

      {/* Link to Invoice Dialog */}
      {showLinkDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 dark:bg-[#1a4f5c]">
            <h3 className="mb-4 text-lg font-bold">{t('invoice.deliveryNote.invoicing')}</h3>
            <div>
              <label className="mb-1 block text-sm font-medium">
                {t('invoice.deliveryNote.invoiceIdLabel')}
              </label>
              <input
                type="text"
                value={invoiceId}
                onChange={e => setInvoiceId(e.target.value)}
                className="w-full rounded-lg border px-3 py-2"
                placeholder={t('invoice.deliveryNote.invoiceIdPlaceholder')}
              />
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowLinkDialog(false);
                  setInvoiceId('');
                }}
                className="rounded-lg border px-4 py-2 hover:bg-gray-50 dark:border-[#2d7d8a] dark:hover:bg-[#0f3d47]"
              >
                {t('invoice.deliveryNote.cancel')}
              </button>
              <button
                onClick={handleLinkToInvoice}
                disabled={!invoiceId || linkMutation.isPending}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {linkMutation.isPending
                  ? t('invoice.deliveryNote.processing')
                  : t('invoice.deliveryNote.assignToInvoice')}
              </button>
            </div>
            {linkMutation.isError && (
              <div className="mt-3 text-sm text-red-600">
                {t('invoice.deliveryNote.errorPrefix', {
                  message: linkMutation.error?.message ?? t('invoice.deliveryNote.unknownError'),
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
